import { formatNumber, FormatNumberOptions } from 'utils/utilities'

type ProgressBar = {
  value: number
  valueFormat?: FormatNumberOptions
  barColor: string
  tickColor?: string
  showCurrentValue?: boolean
}

type CustomProgressBarProps = {
  displayMode?: 'normal' | 'overlay'
  size?: 'sm' | 'md' | 'lg' | 'xl'
  progressBar: (ProgressBar | null)[]
  range: [number, number]
  rangeFormat?: FormatNumberOptions
  baseColor?: string
  showRange?: boolean
}

export const CustomProgressBar = (props: CustomProgressBarProps) => {
  const {
    displayMode = 'normal',
    size = 'sm',
    progressBar,
    range,
    rangeFormat,
    baseColor = '#f3f4f6',
    showRange,
  } = props

  const minRange = range[0]
  const maxRange = range[1]

  const getWidth = (value: number) => {
    return (Math.min(value, maxRange) / maxRange) * 100
  }

  const getHeight = () => {
    switch (size) {
      case 'sm':
        return 12
      case 'md':
        return 16
      case 'lg':
        return 22
      case 'xl':
        return 26
    }
  }

  const getGap = (offset: number) => {
    if (displayMode === 'normal') return 0

    switch (size) {
      case 'sm':
      case 'md':
      case 'lg':
      case 'xl':
        return offset * 1
    }
  }

  const getBorderRadius = () => {
    switch (size) {
      case 'sm':
      case 'md':
        return 12
      case 'lg':
      case 'xl':
        return 6
    }
  }

  const getRightBorderRadius = (value: number) => {
    const remaining = 100 - getWidth(value)
    const radius = remaining < 1 ? getBorderRadius() - getBorderRadius() * remaining : 0
    return radius
  }

  return (
    <div className="flex flex-row w-full items-center" style={{ height: `${getHeight() + 72}px` }}>
      {!!showRange && <span className="text-sm font-semibold mr-1">{formatNumber(minRange, rangeFormat) ?? '-'}</span>}

      <div className="flex relative w-full items-center" style={{ height: `${getHeight()}px` }}>
        <div
          className="flex absolute w-full shadow-md"
          style={{
            backgroundColor: baseColor,
            height: `${getHeight()}px`,
            borderRadius: getBorderRadius(),
          }}
        />
        {progressBar.map(
          (bar, index) =>
            !!bar && (
              <div
                key={'progress-bar-' + index}
                className="flex absolute shadow-md"
                style={{
                  backgroundColor: bar.barColor,
                  left: getGap(index),
                  width: `calc(${getWidth(bar.value)}% - ${getGap(index * 2)}px)`,
                  height: `${getHeight() - getGap(index) * 2}px`,
                  borderTopLeftRadius: getBorderRadius(),
                  borderBottomLeftRadius: getBorderRadius(),
                  borderTopRightRadius: getRightBorderRadius(bar.value),
                  borderBottomRightRadius: getRightBorderRadius(bar.value),
                }}
              >
                {bar.showCurrentValue && (
                  <>
                    <span
                      className="flex absolute right-0 border-r-2 border-dotted z-10"
                      style={{
                        top: index % 2 === 0 ? 'auto' : 0,
                        bottom: index % 2 === 0 ? 0 : 'auto',
                        borderColor: bar.tickColor ?? bar.barColor,
                        height: `${getHeight() + 12}px`,
                      }}
                    />
                    <span
                      className="flex absolute -right-4 text-sm font-semibold"
                      style={{
                        top: index % 2 === 0 ? 'auto' : getHeight() + 12,
                        bottom: index % 2 === 0 ? getHeight() + 12 : 'auto',
                      }}
                    >
                      {formatNumber(bar.value, bar.valueFormat) ?? '-'}
                    </span>
                  </>
                )}
              </div>
            )
        )}
      </div>

      {!!showRange && <span className="text-sm font-semibold ml-1">{formatNumber(maxRange, rangeFormat) ?? '-'}</span>}
    </div>
  )
}
